import PropTypes from 'prop-types';
import React from 'react';

import {
  ColorSchema,
  colorSchemaList,
  ComponentSize,
  componentSizeList,
  FontSize,
  fontSizeList,
} from '../../../typings/design-tokens';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import SkeletonContainer from '../../atoms/skeleton-container/skeleton-container';
import SkeletonWrapper from '../../atoms/skeleton-wrapper/skeleton-wrapper';
import Skeleton from '../../atoms/skeleton/skeleton';
import MatchRow, { IMatchRow } from '../../molecules/match-row/match-row';
import { TeamLogoSize, teamLogoSizes } from '../../molecules/team/team';
import LmpBlock from '../../organisms/lmp-block/lmp-block';

import { getSkeletonSize } from './match-card-data-block-utils';
import './match-card-data-block.scss';

export interface IMatchDataBlockLayout {
  colorSchema?: ColorSchema;
  fontSize?: FontSize;
  logoSize?: TeamLogoSize;
  size?: ComponentSize;
}
export interface IMatchCardDataBlock {
  match: IMatchRow;
  layout?: IMatchDataBlockLayout;
  isLoading?: boolean;
  countdownEnabled?: boolean;
}

const classNames = (colorSchema?: ColorSchema): IClassNames =>
  makeClassNames({
    MatchCardDataBlock: ['match-card-data-block', colorSchema === 'dark-light' && 'match-card-data-block--dark-light'],
  });

const MatchCardDataBlock: React.FC<IMatchCardDataBlock> = ({
  match,
  countdownEnabled,
  /* istanbul ignore next: default value  */ isLoading = true,
  /* istanbul ignore next: default value  */ layout = {
    colorSchema: 'dark-light',
    fontSize: '16--14',
    logoSize: '30',
    size: 'small',
  },
}) => {
  const { colorSchema, fontSize, logoSize, size } = layout;
  const cn = classNames(colorSchema);
  return (
    <div className={cn.MatchCardDataBlock}>
      {!isLoading ? (
        <LmpBlock
          match={match}
          colorSchema={colorSchema}
          variant="data-card"
          fontSize={fontSize}
          logoSize={logoSize}
          resultLabelSize={size}
          countdownEnabled={countdownEnabled}
        />
      ) : (
        <SkeletonWrapper height="100%" isFlex isCentered>
          <SkeletonContainer flexSize={getSkeletonSize(size).icon.flexSize} isVertical isCentered>
            <Skeleton
              width={getSkeletonSize(size).icon.size}
              height={getSkeletonSize(size).icon.size}
              spaceBottom={getSkeletonSize(size).icon.spaceBottom}
              colorSchema="dark-light"
            />
            <Skeleton
              fontSize={getSkeletonSize(size).icon.fontSize}
              width={getSkeletonSize(size).icon.fontWidth}
              colorSchema="dark-light"
              isText
            />
          </SkeletonContainer>
          <SkeletonContainer flexSize={getSkeletonSize(size).label.flexSize} flexJustify="center" isCentered>
            <Skeleton
              fontSize={getSkeletonSize(size).label.fontSize}
              width={getSkeletonSize(size).label.width}
              colorSchema="dark-light"
              isText
            />
          </SkeletonContainer>
          <SkeletonContainer flexSize={getSkeletonSize(size).icon.flexSize} isVertical isCentered>
            <Skeleton
              width={getSkeletonSize(size).icon.size}
              height={getSkeletonSize(size).icon.size}
              spaceBottom={getSkeletonSize(size).icon.spaceBottom}
              colorSchema="dark-light"
            />
            <Skeleton
              fontSize={getSkeletonSize(size).icon.fontSize}
              width={getSkeletonSize(size).icon.fontWidth}
              colorSchema="dark-light"
              isText
            />
          </SkeletonContainer>
        </SkeletonWrapper>
      )}
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchCardDataBlock.propTypes = {
    countdownEnabled: PropTypes.bool,
    isLoading: PropTypes.bool,
    layout: PropTypes.shape({
      colorSchema: PropTypes.oneOf([...colorSchemaList, undefined]).isRequired,
      fontSize: PropTypes.oneOf([...fontSizeList, undefined]).isRequired,
      logoSize: PropTypes.oneOf([...teamLogoSizes, undefined]).isRequired,
      size: PropTypes.oneOf([...componentSizeList, undefined]).isRequired,
    }),
    match: PropTypes.shape(MatchRow.propTypes!).isRequired,
  };
}

export default MatchCardDataBlock;
